// =====================================================
// 产品矩阵系统 - 画布状态管理（含内置历史记录）
// 每个操作自动记录，支持精确的撤销/重做
// =====================================================

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type {
  LayoutType,
  StyleType,
  CanvasData,
  CanvasCell,
  CanvasSettings,
  CanvasProject,
  Product,
  ProductModel,
} from '../types';
import { HISTORY_CONFIG } from '../constants';

// =====================================================
// 类型定义
// =====================================================

/** 历史快照 */
interface Snapshot {
  id: string;
  timestamp: number;
  description: string;
  layoutType: LayoutType;
  styleType: StyleType;
  canvasData: CanvasData;
  customRows: number;
  customCols: number;
  columnLabels: string[];
  rowLabels: string[];
}

interface CanvasState {
  // 当前画布配置
  layoutType: LayoutType;
  styleType: StyleType;
  canvasData: CanvasData;
  
  // 自定义布局
  customRows: number;
  customCols: number;
  
  // 坐标轴标题
  columnLabels: string[];
  rowLabels: string[];
  showAxisLabels: boolean;
  
  // 卡片样式控制（独立于风格）
  showBorder: boolean;           // 是否显示卡片边框
  showCardBackground: boolean;   // 是否显示卡片背景色
  showCanvasBackground: boolean; // 是否显示画布背景
  canvasBackgroundColor: string; // 画布背景颜色
  
  // 卡片内容显示设置
  cardContentSettings: {
    showTitle: boolean;          // 显示标题
    showModelRange: boolean;     // 显示型号范围
    showSlogan: boolean;         // 显示 Slogan/场景
    showFeatures: boolean;       // 显示特点列表
    featuresCount: number;       // 显示特点数量 (1-5)
    titleColor: string;          // 标题颜色
    sloganColor: string;         // Slogan 颜色
    featureColor: string;        // 特点颜色
    textPaddingLeft: number;     // 文字区域左侧偏移 (0-20%)
  };
  
  // 项目信息
  currentProjectId: string | undefined;
  currentProjectName: string;
  isDirty: boolean;
  
  // 历史记录（内置）
  undoStack: Snapshot[];
  redoStack: Snapshot[];
  isPerformingHistoryAction: boolean;
  
  // UI状态
  selectedCellIndex: number | null;
  selectedCells: number[];           // 多选的单元格索引
  isMergeMode: boolean;              // 是否处于合并模式
  isExporting: boolean;
  
  // ===== 布局/风格操作 =====
  setLayoutType: (type: LayoutType) => void;
  setStyleType: (type: StyleType) => void;
  setCustomLayout: (rows: number, cols: number) => void;
  
  // ===== 坐标轴操作 =====
  setColumnLabel: (index: number, label: string) => void;
  setRowLabel: (index: number, label: string) => void;
  toggleAxisLabels: () => void;
  
  // ===== 卡片样式控制 =====
  toggleBorder: () => void;
  toggleCardBackground: () => void;
  toggleCanvasBackground: () => void;
  setCanvasBackgroundColor: (color: string) => void;
  updateCardContentSettings: (settings: Partial<CanvasState['cardContentSettings']>) => void;
  
  // ===== 单元格操作 =====
  addProductToCell: (cellIndex: number, product: Product, model?: ProductModel) => void;
  removeProductFromCell: (cellIndex: number) => void;
  updateCell: (cellIndex: number, updates: Partial<CanvasCell>) => void;
  selectCell: (index: number | null) => void;
  swapCells: (fromIndex: number, toIndex: number) => void;
  
  // ===== 单元格合并操作 =====
  toggleMergeMode: () => void;
  toggleCellSelection: (index: number) => void;
  clearCellSelection: () => void;
  mergeCells: () => void;
  unmergeCells: (cellIndex: number) => void;
  
  // ===== 画布操作 =====
  clearCanvas: () => void;
  updateSettings: (settings: Partial<CanvasSettings>) => void;
  
  // ===== 历史操作 =====
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clearHistory: () => void;
  
  // ===== 项目操作 =====
  loadProject: (project: CanvasProject) => void;
  setProjectInfo: (id: string | undefined, name: string) => void;
  newProject: () => void;
  markAsSaved: () => void;
  
  // ===== 工具方法 =====
  getCurrentConfig: () => { rows: number; cols: number };
  getCellCount: () => number;
  getFilledCellCount: () => number;
  getCellByIndex: (index: number) => CanvasCell | undefined;
  getHistoryInfo: () => { undoCount: number; redoCount: number; lastAction?: string };
}

// =====================================================
// 工具函数
// =====================================================

const generateId = () => `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

const deepClone = <T>(obj: T): T => JSON.parse(JSON.stringify(obj));

const layoutConfigs: Record<Exclude<LayoutType, 'grid_custom'>, { rows: number; cols: number }> = {
  grid_2x2: { rows: 2, cols: 2 },
  grid_3x3: { rows: 3, cols: 3 },
  grid_4x4: { rows: 4, cols: 4 },
  grid_9x9: { rows: 9, cols: 9 },
};

const initializeCells = (rows: number, cols: number): CanvasCell[] => {
  const cellCount = rows * cols;
  return Array.from({ length: cellCount }, (_, index) => ({
    index,
    row: Math.floor(index / cols),
    col: index % cols,
  }));
};

const defaultSettings: CanvasSettings = {
  backgroundColor: '#ffffff',
  padding: 20,
  gap: 10,
  watermark: { enabled: false, position: 'bottom-right', opacity: 0.5 },
  branding: { enabled: false, position: 'bottom' },
};

const initializeLabels = (count: number, prefix: string): string[] => {
  return Array.from({ length: count }, (_, i) => `${prefix}${i + 1}`);
};

// =====================================================
// Store
// =====================================================

export const useCanvasStore = create<CanvasState>()(
  subscribeWithSelector((set, get) => {
    
    // 创建快照
    const createSnapshot = (description: string): Snapshot => {
      const state = get();
      return {
        id: generateId(),
        timestamp: Date.now(),
        description,
        layoutType: state.layoutType,
        styleType: state.styleType,
        canvasData: deepClone(state.canvasData),
        customRows: state.customRows,
        customCols: state.customCols,
        columnLabels: [...state.columnLabels],
        rowLabels: [...state.rowLabels],
      };
    };
    
    // 保存快照到撤销栈
    const saveSnapshot = (description: string) => {
      const state = get();
      if (state.isPerformingHistoryAction) return;
      
      const snapshot = createSnapshot(description);
      const newUndoStack = [...state.undoStack, snapshot];
      
      // 限制栈大小
      if (newUndoStack.length > HISTORY_CONFIG.maxUndoSteps) {
        newUndoStack.shift();
      }
      
      set({
        undoStack: newUndoStack,
        redoStack: [], // 新操作清空重做栈
      });
    };
    
    // 恢复快照
    const restoreSnapshot = (snapshot: Snapshot) => {
      const config = snapshot.layoutType === 'grid_custom' 
        ? { rows: snapshot.customRows, cols: snapshot.customCols }
        : layoutConfigs[snapshot.layoutType];
      
      set({
        layoutType: snapshot.layoutType,
        styleType: snapshot.styleType,
        canvasData: deepClone(snapshot.canvasData),
        customRows: snapshot.customRows,
        customCols: snapshot.customCols,
        columnLabels: [...snapshot.columnLabels],
        rowLabels: [...snapshot.rowLabels],
        isDirty: true,
      });
    };
    
    return {
      // ===== 初始状态 =====
      layoutType: 'grid_3x3',
      styleType: 'standard',
      canvasData: {
        cells: initializeCells(3, 3),
        settings: { ...defaultSettings },
      },
      customRows: 3,
      customCols: 5,
      columnLabels: initializeLabels(3, '列'),
      rowLabels: initializeLabels(3, '行'),
      showAxisLabels: false,
      showBorder: true,           // 默认显示边框
      showCardBackground: true,   // 默认显示卡片背景色
      showCanvasBackground: true, // 默认显示画布背景
      canvasBackgroundColor: '#ffffff', // 默认白色背景
      
      // 卡片内容显示设置默认值
      cardContentSettings: {
        showTitle: true,
        showModelRange: true,
        showSlogan: true,
        showFeatures: true,
        featuresCount: 3,
        titleColor: '',           // 空字符串表示使用默认色
        sloganColor: '#22c55e',   // 默认绿色
        featureColor: '',         // 空字符串表示使用默认色
        textPaddingLeft: 8,       // 默认 8%
      },
      
      currentProjectId: undefined,
      currentProjectName: '未命名项目',
      isDirty: false,
      
      undoStack: [],
      redoStack: [],
      isPerformingHistoryAction: false,
      
      selectedCellIndex: null,
      selectedCells: [],              // 多选单元格
      isMergeMode: false,             // 合并模式
      isExporting: false,
      
      // ===== 布局操作 =====
      setLayoutType: (type) => {
        saveSnapshot('更改布局');
        
        const state = get();
        const config = type === 'grid_custom' 
          ? { rows: state.customRows, cols: state.customCols }
          : layoutConfigs[type];
        
        const newCells = initializeCells(config.rows, config.cols);
        const oldCells = state.canvasData.cells;
        
        // 保留现有数据
        newCells.forEach((cell, index) => {
          if (oldCells[index]?.product_id) {
            cell.product_id = oldCells[index].product_id;
            cell.model_id = oldCells[index].model_id;
            cell.custom_text = oldCells[index].custom_text;
            cell.custom_image = oldCells[index].custom_image;
          }
        });
        
        const newColumnLabels = Array.from({ length: config.cols }, (_, i) => 
          state.columnLabels[i] || `列${i + 1}`
        );
        const newRowLabels = Array.from({ length: config.rows }, (_, i) => 
          state.rowLabels[i] || `行${i + 1}`
        );
        
        set({
          layoutType: type,
          canvasData: { ...state.canvasData, cells: newCells },
          columnLabels: newColumnLabels,
          rowLabels: newRowLabels,
          isDirty: true,
          selectedCellIndex: null,
        });
      },
      
      setStyleType: (type) => {
        saveSnapshot('更改风格');
        set({ styleType: type, isDirty: true });
      },
      
      setCustomLayout: (rows, cols) => {
        saveSnapshot('自定义布局');
        
        const state = get();
        const newCells = initializeCells(rows, cols);
        const oldCells = state.canvasData.cells;
        
        newCells.forEach((cell, index) => {
          if (oldCells[index]?.product_id) {
            cell.product_id = oldCells[index].product_id;
            cell.model_id = oldCells[index].model_id;
            cell.custom_text = oldCells[index].custom_text;
            cell.custom_image = oldCells[index].custom_image;
          }
        });
        
        const newColumnLabels = Array.from({ length: cols }, (_, i) => 
          state.columnLabels[i] || `列${i + 1}`
        );
        const newRowLabels = Array.from({ length: rows }, (_, i) => 
          state.rowLabels[i] || `行${i + 1}`
        );
        
        set({
          layoutType: 'grid_custom',
          customRows: rows,
          customCols: cols,
          canvasData: { ...state.canvasData, cells: newCells },
          columnLabels: newColumnLabels,
          rowLabels: newRowLabels,
          isDirty: true,
          selectedCellIndex: null,
        });
      },
      
      // ===== 坐标轴操作 =====
      setColumnLabel: (index, label) => {
        saveSnapshot('修改列标题');
        set((state) => {
          const newLabels = [...state.columnLabels];
          newLabels[index] = label;
          return { columnLabels: newLabels, isDirty: true };
        });
      },
      
      setRowLabel: (index, label) => {
        saveSnapshot('修改行标题');
        set((state) => {
          const newLabels = [...state.rowLabels];
          newLabels[index] = label;
          return { rowLabels: newLabels, isDirty: true };
        });
      },
      
      toggleAxisLabels: () => {
        set((state) => ({ showAxisLabels: !state.showAxisLabels }));
      },
      
      // ===== 卡片样式控制 =====
      toggleBorder: () => {
        set((state) => ({ showBorder: !state.showBorder }));
      },
      
      toggleCardBackground: () => {
        set((state) => ({ showCardBackground: !state.showCardBackground }));
      },
      
      toggleCanvasBackground: () => {
        set((state) => ({ showCanvasBackground: !state.showCanvasBackground }));
      },
      
      setCanvasBackgroundColor: (color) => {
        set({ canvasBackgroundColor: color, showCanvasBackground: true });
      },
      
      updateCardContentSettings: (settings) => {
        set((state) => ({
          cardContentSettings: { ...state.cardContentSettings, ...settings },
          isDirty: true,
        }));
      },
      
      // ===== 单元格操作 =====
      addProductToCell: (cellIndex, product, model) => {
        saveSnapshot(`添加产品到格子 ${cellIndex + 1}`);
        
        set((state) => {
          const newCells = [...state.canvasData.cells];
          if (newCells[cellIndex]) {
            newCells[cellIndex] = {
              ...newCells[cellIndex],
              product_id: product.id,
              model_id: model?.id,
            };
          }
          return {
            canvasData: { ...state.canvasData, cells: newCells },
            isDirty: true,
          };
        });
      },
      
      removeProductFromCell: (cellIndex) => {
        saveSnapshot(`移除格子 ${cellIndex + 1} 的产品`);
        
        set((state) => {
          const newCells = [...state.canvasData.cells];
          if (newCells[cellIndex]) {
            // 保留合并信息（rowSpan/colSpan），只清除产品相关数据
            newCells[cellIndex] = {
              index: cellIndex,
              row: newCells[cellIndex].row,
              col: newCells[cellIndex].col,
              // 保留合并状态
              rowSpan: newCells[cellIndex].rowSpan,
              colSpan: newCells[cellIndex].colSpan,
              // 不保留产品相关信息：product_id, model_id, custom_text, custom_image
            };
          }
          return {
            canvasData: { ...state.canvasData, cells: newCells },
            isDirty: true,
            selectedCellIndex: state.selectedCellIndex === cellIndex ? null : state.selectedCellIndex,
          };
        });
      },
      
      updateCell: (cellIndex, updates) => {
        saveSnapshot(`更新格子 ${cellIndex + 1}`);
        
        set((state) => {
          const newCells = [...state.canvasData.cells];
          if (newCells[cellIndex]) {
            newCells[cellIndex] = { ...newCells[cellIndex], ...updates };
          }
          return {
            canvasData: { ...state.canvasData, cells: newCells },
            isDirty: true,
          };
        });
      },
      
      selectCell: (index) => {
        set({ selectedCellIndex: index });
      },
      
      swapCells: (fromIndex, toIndex) => {
        saveSnapshot(`交换格子 ${fromIndex + 1} 和 ${toIndex + 1}`);
        
        set((state) => {
          const newCells = [...state.canvasData.cells];
          const fromCell = newCells[fromIndex];
          const toCell = newCells[toIndex];
          
          if (fromCell && toCell) {
            newCells[fromIndex] = {
              ...fromCell,
              product_id: toCell.product_id,
              model_id: toCell.model_id,
              custom_text: toCell.custom_text,
              custom_image: toCell.custom_image,
              layout: toCell.layout,
            };
            newCells[toIndex] = {
              ...toCell,
              product_id: fromCell.product_id,
              model_id: fromCell.model_id,
              custom_text: fromCell.custom_text,
              custom_image: fromCell.custom_image,
              layout: fromCell.layout,
            };
          }
          
          return {
            canvasData: { ...state.canvasData, cells: newCells },
            isDirty: true,
          };
        });
      },
      
      // ===== 单元格合并操作 =====
      toggleMergeMode: () => {
        set((state) => ({
          isMergeMode: !state.isMergeMode,
          selectedCells: !state.isMergeMode ? [] : state.selectedCells,
        }));
      },
      
      toggleCellSelection: (index) => {
        set((state) => {
          const cell = state.canvasData.cells[index];
          // 不允许选择已被合并的单元格或有产品的单元格
          if (cell?.hidden || cell?.product_id) return state;
          
          const isSelected = state.selectedCells.includes(index);
          return {
            selectedCells: isSelected
              ? state.selectedCells.filter(i => i !== index)
              : [...state.selectedCells, index],
          };
        });
      },
      
      clearCellSelection: () => {
        set({ selectedCells: [] });
      },
      
      mergeCells: () => {
        const state = get();
        const { selectedCells, canvasData, customRows, customCols, layoutType } = state;
        
        if (selectedCells.length < 2) return;
        
        // 获取布局配置
        const config = layoutType === 'grid_custom'
          ? { rows: customRows, cols: customCols }
          : layoutConfigs[layoutType];
        
        // 计算选中单元格的边界
        const selectedCellsData = selectedCells.map(i => canvasData.cells[i]);
        const minRow = Math.min(...selectedCellsData.map(c => c.row));
        const maxRow = Math.max(...selectedCellsData.map(c => c.row));
        const minCol = Math.min(...selectedCellsData.map(c => c.col));
        const maxCol = Math.max(...selectedCellsData.map(c => c.col));
        
        // 检查是否是矩形区域
        const expectedCount = (maxRow - minRow + 1) * (maxCol - minCol + 1);
        if (selectedCells.length !== expectedCount) {
          console.warn('选中的单元格必须形成矩形区域');
          return;
        }
        
        // 检查是否有已合并的单元格
        if (selectedCellsData.some(c => c.rowSpan && c.rowSpan > 1 || c.colSpan && c.colSpan > 1)) {
          console.warn('不能合并已经合并过的单元格');
          return;
        }
        
        saveSnapshot('合并单元格');
        
        // 找到左上角的单元格作为主单元格
        const mainCellIndex = canvasData.cells.findIndex(
          c => c.row === minRow && c.col === minCol
        );
        
        set((state) => {
          const newCells = [...state.canvasData.cells];
          
          // 设置主单元格的 span
          newCells[mainCellIndex] = {
            ...newCells[mainCellIndex],
            rowSpan: maxRow - minRow + 1,
            colSpan: maxCol - minCol + 1,
          };
          
          // 隐藏其他单元格
          selectedCells.forEach(i => {
            if (i !== mainCellIndex) {
              newCells[i] = {
                ...newCells[i],
                hidden: true,
                mergedInto: mainCellIndex,
              };
            }
          });
          
          return {
            canvasData: { ...state.canvasData, cells: newCells },
            selectedCells: [],
            isMergeMode: false,
            isDirty: true,
          };
        });
      },
      
      unmergeCells: (cellIndex) => {
        const state = get();
        const cell = state.canvasData.cells[cellIndex];
        
        if (!cell?.rowSpan || cell.rowSpan === 1 && (!cell.colSpan || cell.colSpan === 1)) {
          return; // 不是合并的单元格
        }
        
        saveSnapshot('取消合并单元格');
        
        set((state) => {
          const newCells = [...state.canvasData.cells];
          
          // 重置主单元格
          newCells[cellIndex] = {
            ...newCells[cellIndex],
            rowSpan: undefined,
            colSpan: undefined,
            product_id: undefined,
            model_id: undefined,
          };
          
          // 取消隐藏被合并的单元格
          newCells.forEach((c, i) => {
            if (c.mergedInto === cellIndex) {
              newCells[i] = {
                ...c,
                hidden: undefined,
                mergedInto: undefined,
              };
            }
          });
          
          return {
            canvasData: { ...state.canvasData, cells: newCells },
            isDirty: true,
          };
        });
      },

      // ===== 画布操作 =====
      clearCanvas: () => {
        saveSnapshot('清空画布');
        
        const state = get();
        const config = state.layoutType === 'grid_custom'
          ? { rows: state.customRows, cols: state.customCols }
          : layoutConfigs[state.layoutType];
        
        set({
          canvasData: {
            cells: initializeCells(config.rows, config.cols),
            settings: { ...defaultSettings },
          },
          isDirty: true,
          selectedCellIndex: null,
        });
      },
      
      updateSettings: (settings) => {
        saveSnapshot('更新设置');
        set((state) => ({
          canvasData: {
            ...state.canvasData,
            settings: { ...state.canvasData.settings, ...settings },
          },
          isDirty: true,
        }));
      },
      
      // ===== 历史操作 =====
      undo: () => {
        const state = get();
        if (state.undoStack.length === 0) return;
        
        // 保存当前状态到重做栈
        const currentSnapshot = createSnapshot('当前状态');
        const newRedoStack = [...state.redoStack, currentSnapshot];
        if (newRedoStack.length > HISTORY_CONFIG.maxRedoSteps) {
          newRedoStack.shift();
        }
        
        // 获取并恢复上一个状态
        const previousSnapshot = state.undoStack[state.undoStack.length - 1];
        const newUndoStack = state.undoStack.slice(0, -1);
        
        set({ isPerformingHistoryAction: true });
        restoreSnapshot(previousSnapshot);
        set({
          undoStack: newUndoStack,
          redoStack: newRedoStack,
          isPerformingHistoryAction: false,
        });
      },
      
      redo: () => {
        const state = get();
        if (state.redoStack.length === 0) return;
        
        // 保存当前状态到撤销栈
        const currentSnapshot = createSnapshot('当前状态');
        const newUndoStack = [...state.undoStack, currentSnapshot];
        
        // 获取并恢复下一个状态
        const nextSnapshot = state.redoStack[state.redoStack.length - 1];
        const newRedoStack = state.redoStack.slice(0, -1);
        
        set({ isPerformingHistoryAction: true });
        restoreSnapshot(nextSnapshot);
        set({
          undoStack: newUndoStack,
          redoStack: newRedoStack,
          isPerformingHistoryAction: false,
        });
      },
      
      canUndo: () => get().undoStack.length > 0,
      canRedo: () => get().redoStack.length > 0,
      
      clearHistory: () => {
        set({ undoStack: [], redoStack: [] });
      },
      
      // ===== 项目操作 =====
      loadProject: (project) => {
        set({
          layoutType: project.layout_type,
          styleType: project.style_type,
          canvasData: deepClone(project.canvas_data),
          currentProjectId: project.id,
          currentProjectName: project.name,
          isDirty: false,
          undoStack: [],
          redoStack: [],
          selectedCellIndex: null,
        });
      },
      
      setProjectInfo: (id, name) => {
        set({ currentProjectId: id, currentProjectName: name });
      },
      
      newProject: () => {
        set({
          layoutType: 'grid_3x3',
          styleType: 'standard',
          canvasData: {
            cells: initializeCells(3, 3),
            settings: { ...defaultSettings },
          },
          customRows: 3,
          customCols: 5,
          columnLabels: initializeLabels(3, '列'),
          rowLabels: initializeLabels(3, '行'),
          currentProjectId: undefined,
          currentProjectName: '未命名项目',
          isDirty: false,
          undoStack: [],
          redoStack: [],
          selectedCellIndex: null,
        });
      },
      
      markAsSaved: () => {
        set({ isDirty: false });
      },
      
      // ===== 工具方法 =====
      getCurrentConfig: () => {
        const state = get();
        if (state.layoutType === 'grid_custom') {
          return { rows: state.customRows, cols: state.customCols };
        }
        return layoutConfigs[state.layoutType];
      },
      
      getCellCount: () => {
        const state = get();
        const config = state.getCurrentConfig();
        return config.rows * config.cols;
      },
      
      getFilledCellCount: () => {
        return get().canvasData.cells.filter((cell) => cell.product_id).length;
      },
      
      getCellByIndex: (index) => {
        return get().canvasData.cells[index];
      },
      
      getHistoryInfo: () => {
        const state = get();
        const lastSnapshot = state.undoStack[state.undoStack.length - 1];
        return {
          undoCount: state.undoStack.length,
          redoCount: state.redoStack.length,
          lastAction: lastSnapshot?.description,
        };
      },
    };
  })
);

// 导出类型
export type { Snapshot };
